import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Pencil } from 'lucide-react';
import Modal from './Modal';
import { fetchSourceInfoList, createSourceInfo, updateAirlineConfiguration } from '../api/sourceInfoApi';
import { formatDate, formatUserName } from '../utils/formatters';
import './SourceInfo.css';

// The backend signals "record already exists" by returning Result = 0 from a
// stored procedure, but the controller actions that wrap it (Save/Edit) are
// untyped IActionResult with no [ProducesResponseType] — swagger has zero
// schema info for the response body, so the exact wire shape/casing isn't
// confirmed. This checks the plausible variants a 200 OK response could use:
// a bare 0, an object with a result/Result property, or — for the Add New
// endpoint specifically, whose response is a SourceInfo-shaped record with an
// id — an id/Id of 0, the standard "nothing was actually inserted" signal for
// an insert-and-return-the-new-id pattern. If a real "already exists"
// response turns out to look different from all of these, this needs
// tightening to match it exactly.
const isDuplicateRecordResult = (responseData) => {
  if (responseData === 0) return true;
  if (responseData && typeof responseData === 'object') {
    return [responseData.result, responseData.Result, responseData.id, responseData.Id].some(
      (v) => v === 0
    );
  }
  return false;
};

const EMPTY_FORM = {
  name: '',
  code: '',
  url: '',
 // gds: 0,
  isActive: true,
  isSimplified: false,
  operatingCarrier: '',
};

function SourceInfo({ accessToken }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  // { message, type: 'warning' | 'error' } | null
  const [toast, setToast] = useState(null);

  // Auto-dismiss the toast a few seconds after it's shown; also cleans up
  // the timer if a new toast replaces it or the component unmounts.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const loadSourceInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSourceInfoList();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch source info');
      console.error('SourceInfo error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSourceInfo();
  }, [loadSourceInfo]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.code.trim()) e.code = 'Code is required.';
    if (!form.url.trim()) e.url = 'URL is required.';
    else { try { new URL(form.url); } catch { e.url = 'Please enter a valid URL.'; } }
    return e;
  };

  const handleOpenAdd = () => {
    setEditingRecord(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setIsModalOpen(true);
  };

  const handleOpenEdit = (record) => {
    // editingRecord (and its original .code) is kept as-opened, separate from
    // form.code which the user may edit, so the update request always
    // identifies the original record regardless of what Code is changed to.
    setEditingRecord(record);
    setForm({
      name: record.name || '',
      code: record.code || '',
      url: record.url || '',
     // gds: record.gds ?? 0,
      isActive: !!record.isActive,
      isSimplified: !!record.isSimplified,
      operatingCarrier: record.operatingCarrier || '',
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setErrors({});
    setIsModalOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const fieldErrors = validate();
    if (Object.keys(fieldErrors).length > 0) { setErrors(fieldErrors); return; }
    setErrors({});

   /* const gdsRaw = form.gds;
    const gdsValue = Number(gdsRaw);
    const gdsIsValid =
      gdsRaw !== '' && gdsRaw !== null && gdsRaw !== undefined &&
      !Number.isNaN(gdsValue) && (gdsValue === 0 || gdsValue === 1);

    if (!gdsIsValid) {
      alert('GDS must be either 0 or 1.');
      return;
    } */

    setSaving(true);

    const payload = {
      name: form.name,
      code: form.code,
      url: form.url,
      //gds: gdsValue,
      isActive: form.isActive,
      isSimplified: form.isSimplified,
      operatingCarrier: form.operatingCarrier?.trim() || null,
    };

    try {
      if (editingRecord) {
        // POST /api/AirlineRefreshConfiguration/Config expects AirlineRefreshModel:
        // { Airline, Code, Url, OperatingCarrier, IsSimplified, IsActive }.
        // Airline is the current (possibly just-edited) Name field, same as
        // every other field here — it must reflect what's on screen when
        // Save is clicked, not a snapshot from when the modal was opened.
        const airlinePayload = {
          Airline: form.name,
          Code: form.code,
          Url: form.url,
          // AirlineRefreshModel.OperatingCarrier is a non-nullable string on the
          // backend — sending JSON null fails model validation ("One or more
          // validation errors occurred"), so an empty string is sent instead
          // to represent "no value" for this specific endpoint.
          OperatingCarrier: form.operatingCarrier?.trim() || '',
          IsSimplified: Number(form.isSimplified),
          IsActive: Number(form.isActive),
        };
        const updateResult = await updateAirlineConfiguration(airlinePayload);
        if (isDuplicateRecordResult(updateResult)) {
          // Stop the Edit flow: no success message, keep the form open as-is.
          // Still reload from the backend so the table reflects the real
          // database state, not any optimistic assumption.
          setToast({ message: 'Record already exists.', type: 'warning' });
          await loadSourceInfo();
          setSaving(false);
          return;
        }
        // The endpoint only returns { statusCode, message }, not the updated
        // record, so refresh from the server to reflect what actually persisted.
        await loadSourceInfo();
      } else {
        const created = await createSourceInfo(payload);
        if (isDuplicateRecordResult(created)) {
          // Stop the Save flow: no success message, no optimistic append.
          // Reload from the backend — the database is the source of truth,
          // so the table must reflect it even for the duplicate case.
          setToast({ message: 'Record already exists.', type: 'warning' });
          await loadSourceInfo();
          setSaving(false);
          return;
        }
        // Database-confirmed insert: append using what the backend actually
        // returned (falls back to the submitted payload for any field it
        // didn't echo back), not a blind optimistic guess.
        setRecords((prev) => [...prev, { id: created?.id, ...payload, ...created }]);
      }
      setSaving(false);
      setIsModalOpen(false);
      setToast({ message: editingRecord ? 'Record updated successfully.' : 'Record saved successfully.', type: 'success' });
    } catch (err) {
      // Surface the real failure instead of masking it — do not update local
      // state and do not close the modal, so a failed save is never mistaken
      // for a successful one.
      console.error('SourceInfo: save failed', err.response?.data || err.message);
      // ASP.NET Core's default model-validation failure shape is
      // { title: "One or more validation errors occurred.", errors: { Field: ["reason"] } } —
      // surface the per-field detail too, not just the generic title.
      const validationErrors = err.response?.data?.errors;
      const validationDetail = validationErrors
        ? Object.entries(validationErrors)
            .map(([field, messages]) => `${field}: ${[].concat(messages).join(' ')}`)
            .join('\n')
        : null;
      const serverMessage =
        validationDetail ||
        (typeof err.response?.data === 'string' && err.response.data) ||
        err.response?.data?.message ||
        err.response?.data?.title ||
        err.message ||
        'Failed to save source info';
      setToast({ message: `Save failed: ${serverMessage}`, type: 'error' });
      setSaving(false);
    }
  };

  return (
    <section className="source-info-section">
      {toast && (
        <div className={`toast toast--${toast.type}`} role="alert">
          <span className="toast__icon">
            {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
          </span>
          <span className="toast__message">{toast.message}</span>
          <button type="button" className="toast__close" onClick={() => setToast(null)}>&times;</button>
        </div>
      )}

      <div className="container">
        <div className="si-header d-flex">
          <h4 className="si-title">Airline Source Info</h4>
          <button type="button" className="btn btn-primary si-add-btn" onClick={handleOpenAdd}>
            Add New
          </button>
        </div>

        {loading ? (
          <div className="si-loader-wrap w-full justify-content-center">
            <div className="spinner"></div>
            <p className="text text-center">Loading Source Info...</p>
          </div>
        ) : error ? (
          <div className="si-msg-center">
            <div className="warning-wrap alert-msg-box">
              <span className="icon warning-icon"><AlertTriangle size={20} /></span>
              <span className="alert-title">Error: {error}</span>
              <button type="button" className="btn btn-danger si-retry-btn" onClick={loadSourceInfo}>Retry</button>
            </div>
          </div>
        ) : records.length === 0 ? (
          <div className="si-msg-center">
            <div className="warning-wrap alert-msg-box">
              <span className="icon warning-icon"><AlertTriangle size={20} /></span>
              <span className="alert-title">No source info records found</span>
            </div>
          </div>
        ) : (
          <div className="content-box">
            <div className="si-table-wrap data-table-wrap">
              <table className="si-table data-table">
                <thead>
                  <tr>
                    <th className="text-center">Actions</th>
                    <th>Name</th>
                    <th className="text-center">Code</th>
                    <th>Url</th>
                    {/* <th className="text-center">GDS</th> */}
                    <th className="text-center">IsActive</th>
                    <th className="text-center">IsSimplified</th>
                    <th>OperatingCarrier</th>
                    <th>Created By</th>
                    <th>Created On</th>
                    <th>Updated By</th>
                    <th>Updated On</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr key={record.code} className="si-row">
                      <td className="text-center">
                        <button type="button" className="link__primary" onClick={() => handleOpenEdit(record)}>
                          <Pencil size={14} /><span>Edit</span>
                        </button>
                      </td>
                      <td>{record.name}</td>
                      <td className="text-center">{record.code}</td>
                      <td className="si-td-url">{record.url || '-'}</td>
                    {/*   <td className="text-center">{record.gds ?? '-'}</td> */}
                      <td className="text-center">
                        <span className={record.isActive ? 'badge badge-success' : 'badge badge-muted'}>
                          {record.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={record.isSimplified ? 'badge badge-success' : 'badge badge-muted'}>
                          {record.isSimplified ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{record.operatingCarrier || '-'}</td>
                      <td>{formatUserName(record.createdBy)}</td>
                      <td>{formatDate(record.createdOn)}</td>
                      <td>{formatUserName(record.updatedBy)}</td>
                      <td>{formatDate(record.updatedOn)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}
        title={editingRecord ? 'Edit Source Info' : 'Add Source Info'} variant="default" dialogClass="modal__dialog--md" >
        <form className="si-form" onSubmit={handleSave} noValidate>
          {saving && (
            <div className="modal-saving-overlay">
              <span className="dots-loader"><span></span><span></span><span></span></span>
              <span className="saving-label">{editingRecord ? 'Updating...' : 'Saving...'}</span>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              className={`form-input ${errors.name ? ' form-input--error' : ''}`}
              type="text"
              name="name"
              value={form.name}
              onChange={handleFormChange}
            />
            {errors.name && <span className="form-error">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Code</label>
            <input
              className={`form-input ${errors.code ? ' form-input--error' : ''}`}
              type="text"
              name="code"
              value={form.code}
              onChange={handleFormChange}
              disabled={!!editingRecord}
            />
            {errors.code && <span className="form-error">{errors.code}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Url</label>
            <input
              className={`form-input ${errors.url ? ' form-input--error' : ''}`}
              type="url"
              name="url"
              value={form.url}
              onChange={handleFormChange}
              disabled={!!editingRecord}
            />
            {errors.url && <span className="form-error">{errors.url}</span>}
          </div>
{/*
          <div className="form-group">
            <label className="form-label">GDS</label>
            <input
              className="form-input"
              type="number"
              name="gds"
              value={form.gds}
              onChange={handleFormChange}
            />
          </div>
*/}

          <div className="form-group">
            <label className="form-label">Operating Carrier</label>
            <input
              className="form-input"
              type="text"
              name="operatingCarrier"
              value={form.operatingCarrier}
              onChange={handleFormChange}
            />
          </div>

          <div className="form-group--checkbox">
            <div className="custom-toggle-switch">
              <input
                type="checkbox"
                id="chk-isActive"
                name="isActive"
                checked={form.isActive}
                onChange={handleFormChange}
              />
              <label htmlFor="chk-isActive">
                <span className="toggle-slider"></span>
                <span className="label-text">IsActive</span>
              </label>
            </div>

            <div className="custom-toggle-switch">
              <input
                type="checkbox"
                id="chk-isSimplified"
                name="isSimplified"
                checked={form.isSimplified}
                onChange={handleFormChange}
              />
              <label htmlFor="chk-isSimplified">
                <span className="toggle-slider"></span>
                <span className="label-text">IsSimplified</span>
              </label>
            </div>
          </div>

          <div className="form-footer">
            <button type="button" className="btn btn-outline" onClick={handleCloseModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="dots-loader"><span></span><span></span><span></span></span> : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
}

export default SourceInfo;
